import React, { Fragment, useEffect, useState } from 'react';
import { Box, Button, Card, Flex, Heading, Text } from 'rebass/styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import gql from 'graphql-tag';
import styled, { keyframes } from 'styled-components';

import TextInput from '../components/TextInput';
import FormField from '../components/FormField';
import Header from '../components/Header';
import withAnalytics from '../lib/withAnalytics';
import { CURRENT_USER, GET_ANALYTICS_ID } from '../graphql/queries';
import { useMutation } from '@apollo/client';
import { OtpInner } from './login/OtpInner';
import { ILoginInput, ILoginOutput } from '../graphql/types/mutations/login';
import { CleanDom } from '../utils/domCleanup';
import FullLayoutAnimated from '../components/FullLayoutAnimated';
import { quotes } from '../utils/quotes';
import { MotivationalQuote } from '../components/MotivationalQuote';

const schema = Yup.object().shape({
  email: Yup.string()
    .email('invalid email address')
    .required('Required'),
  password: Yup.string()
    .trim()
    .required(),
});

const LOGIN = gql`
  mutation loginProvider($email: String!, $password: String!, $otp: String) {
    loginProvider(email: $email, password: $password, otp: $otp) {
      token
      user {
        id
        accountLocked
      }
      mustImpersonate
    }
  }
`;

const SEND_BY_EMAIL = gql`
  mutation sendProvidersOtpCodeViaEmail($email: String!) {
    sendProvidersOtpCodeViaEmail(email: $email) {
      ok
    }
  }
`;
const Spinner = styled.div`
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-left-color: #2a3d66;
  border-radius: 50%;
  width: 36px;
  height: 36px;
  animation: spin 1s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled(Text)`
  margin-top: 1rem;
  color: #4a5a77;
  font-size: 0.9rem;
`;
const Container = styled(Flex) <{
  isQuotes?: boolean
}>`
  flex:1;
  flex-direction:column ;
  @media (max-width: 1050px) {
    background:transparent !important ;
    ${({ isQuotes }) => isQuotes && `
      display: none;
    `}
  }
`;
const Container2 = styled(Flex)`
  flex:1;
  flex-direction:column ;
background: rgba(255, 255, 255, 0.45);       // semi-transparent white
  backdrop-filter: blur(10px);               // blur the background behind it
  -webkit-backdrop-filter: blur(10px);       // Safari support
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.3); // subtle border
  box-shadow: 0 4px 30px rgba(0, 0, 0, 0.1);  // soft shadow for depth
  @media (max-width: 1050px) {
    background:transparent !important ;
  }
`;
const Cards = styled(Card)`

    background:white;
  @media (min-width: 1051px) {
    background:transparent;
    border: 0 ;
    box-shadow: unset;
    padding: 0 ;
  }
`

const CardContainer = styled(Flex)`
  max-width: 400px;
`;

const StyledLink = styled(Link)`
  font-size: 14px;
  color: #999;
  text-align: right;
`;


const Login: React.FC<{ analytics: any }> = ({ analytics }) => {
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false);
  const [mustEnterOtp, setMustEnterOtp] = useState(false);
  const [storedData, setStoredData] = useState<any>(null);
  const [sendByEmailMutate, { loading, client }] = useMutation(SEND_BY_EMAIL)
  const [loginMutate] = useMutation<ILoginOutput, ILoginInput>(LOGIN)
  useEffect(() => {
    analytics.page();
    localStorage.removeItem('scramble')
    localStorage.removeItem('initialToken');
    sessionStorage.removeItem('lastTimeStamp');
    CleanDom()
  }, [])

  const sendByEmail = async () => {
    const aa = await sendByEmailMutate({
      variables: {
        email: storedData.email,
      },
    });

    if (aa && aa.data && aa.data.sendProvidersOtpCodeViaEmail) {
      toast.success("Email Sent", {
        position: toast.POSITION.TOP_RIGHT,
        autoClose: 5000
      });
    } else {
      toast.error("An error occured", {
        autoClose: 3000
      });
    }
  };

  const handleSubmit = async (values: any) => {
    setIsLoading(true);
    try {
      if (!values.otp) {
        setStoredData({ ...values });
      }
      const { data } = await loginMutate({ variables: { ...values } });

      if (!data?.loginProvider.token) {

        return
      }
      const attemptedRoute = localStorage.getItem('route');

      localStorage.setItem('token', data.loginProvider.token);

      if (!data.loginProvider.mustImpersonate) {
        const aa = await client.query({
          query: GET_ANALYTICS_ID,
          fetchPolicy: 'network-only',
        });

        if (aa && aa.data && aa.data.getAnalyticsProviderId) {
          analytics.Alias(aa.data.getAnalyticsProviderId);
          try {
            const pt = await client.query({
              query: CURRENT_USER,
              fetchPolicy: 'network-only',
            });

            analytics.Identify(aa.data.getAnalyticsProviderId, {
              isProvider: true,
            });
          } catch (error) {
          }
        }

        if (!data.loginProvider.user) {
          navigate('/logout')
          return
        }
      } else {
        setStoredData(null);
        navigate('/impersonate')
        return
      }

      setStoredData(null);

      navigate(attemptedRoute || '/');
    } catch (error: any) {
      setIsLoading(false);

      if (!error.graphQLErrors) {
        return;
      }

      error.graphQLErrors.forEach((error: any) => {
        if (error.message.toLowerCase() === 'error: otp req') {
          setMustEnterOtp(true);
          return;
        }
        if (error.message.toLowerCase() === 'error: account locked') {
          navigate(
            '/account-locked', {
            state: { email: values.email },
          });
        } else {
          toast.error(error.message, {
            autoClose: 3000
          });
        }
      });
    }
  };

  if (!!isLoading) {
    return (
      <FullLayoutAnimated style={{ overflow: 'hidden', zIndex: 10 }}>
        <Flex style={{ width: '100vw', height: '100vh' }}>

          <Container2 alignItems="center" justifyContent="center">
            <Flex alignItems="center" justifyContent="center">
              <MotivationalQuote />
            </Flex>
            <Flex flexDirection={'column'} alignItems="center" justifyContent="center">
              <Spinner />
              <LoadingText>Getting everything ready for you...</LoadingText>
            </Flex>
          </Container2>
        </Flex>
      </FullLayoutAnimated>
    )
  }

  return (
    <FullLayoutAnimated style={{ overflow: 'hidden', zIndex: 10 }}>
      <Flex style={{ width: '100vw', height: '100vh' }}>

        <Container2 >
          <Flex flexDirection="column">
            <Header dark={false} childContainerStyle={{ marginTop: '-16px' }} containerStyle={{ flex: 'initial', marginBottom: '20px' }}>

              <Heading sx={{ marginLeft: '153px' }}>Providers</Heading>
            </Header>
          </Flex>
          <Flex flex={1} mt={'-120px'} alignItems="center" justifyContent="center">
            <CardContainer flex={1} px={3}>
              <Cards p={4}>
                <Flex flexDirection="column">

                  <Fragment>
                    {!mustEnterOtp ? (
                      <>

                        <Box mb={3}>
                          <Heading >
                            Welcome Back!
                          </Heading>
                        </Box>
                        <Formik
                          initialValues={{
                            email: '',
                            password: '',
                          }}
                          isInitialValid={() => {
                            try {
                              const aa = schema.validateSync({
                                email: '',
                                password: '',
                              });

                              if (aa.errors && (aa.errors as any).length) {
                                return false;
                              }
                              return true;
                            } catch (err) {
                              return false;
                            }
                          }}
                          validationSchema={schema}
                          onSubmit={(values) => handleSubmit(values)}
                          render={({ touched, errors, isValid }) => {
                            return (
                              <>
                                <Form>
                                  <Text mb={4} fontWeight={300}>Sign in to access your dashboard and continue changing lives.</Text>
                                  <FormField mb={3} error={touched.email && errors.email}>
                                    <TextInput placeholder="Your email address" type="email" name="email" id="input_email" />
                                  </FormField>

                                  <FormField mb={4} error={touched.password && errors.password}>
                                    <TextInput placeholder="Password" type="password" name="password" id="input_password" />
                                    <StyledLink to="/forgot-password" id="link_forgotPassword">
                                      Forgot Password?
                                    </StyledLink>
                                  </FormField>
                                  <Flex alignItems="center" flexDirection="column" mt={3}>
                                    <Button disabled={!isValid || loading || isLoading} variant="primary" width={'100%'} style={{ borderRadius: '10px' }} type="submit" id="btn_login">
                                      Login
                                    </Button>
                                  </Flex>
                                </Form>

                              </>
                            );
                          }}
                        />
                      </>) : (
                      <OtpInner
                        mutate={(otp: any) => {
                          handleSubmit({ ...storedData, otp });
                        }}
                        sendByEmail={sendByEmail}
                        loading={!!isLoading || !!loading}
                      />
                    )}
                  </Fragment>
                </Flex>
              </Cards>
            </CardContainer>
          </Flex>
        </Container2>

        <Container isQuotes>
          <Flex flex={1} alignItems="center" justifyContent="center">
            <MotivationalQuote />
          </Flex>

        </Container>
      </Flex>
    </FullLayoutAnimated>
  );
};

export default withAnalytics(Login);
